import React, { useState, useEffect, useContext, useCallback, useRef } from 'react'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { EditFooter } from '@/components/organisms/edit-footer'
import { SurveyTab } from '@/components/molecules/settings/survey-tab'
import * as Api from '@/apis/aipass'
import { useHistory, useParams } from 'react-router-dom'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { v4 as uuid } from 'uuid'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AccountContext } from '@/contexts/account'
import { useSettingsEditPageGuardEffect } from '@/hooks/use-settings-edit-guard'

export const SurveyEdit: React.FC = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const { id: urlId } = useParams<{ id: string | undefined }>()
  const { errorHandler } = useErrorHandler()
  const { account } = useContext<any>(AccountContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [title, setTitle] = useState<string>('')
  const [content, setContent] = useState<string>('')
  const [rawContent, setRawContent] = useState<string>()
  const [inputType, setInputType] = useState<'score' | 'choice_single' | 'choice_multiple' | 'textArea'>('choice_single')
  const [inputValue, setInputValue] = useState<string[]>(['お部屋'])
  const [isRequired, setIsRequired] = useState<boolean>(false)
  const [detailLoadedId, setDetailLoadedId] = useState<string>(uuid())
  const [isMounted, setIsMounted] = useState<boolean>(true)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)

  // 各言語のデータを管理するstate
  const [multiLangData, setMultiLangData] = useState<{
    title: { [key: string]: string }
    content: { [key: string]: string }
    inputValue: { [key: string]: string[] }
  }>({
    title: {},
    content: {},
    inputValue: {},
  })
  const [originalData, setOriginalData] = useState<any>(null)

  // 現在の入力値を参照するためのref
  const currentValuesRef = useRef({ title: '', content: '', inputValue: [''] })

  // refを更新するヘルパー関数
  const updateRef = (title: string, content: string, inputValue: string[]) => {
    currentValuesRef.current = { title, content, inputValue }
  }

  const fetchSurvey = async (surveyId: string) => {
    try {
      const res = await Api.fetchSurvey(surveyId)
      // コンポーネントがアンマウントされている場合は処理を停止
      if (!isMounted) return

      // 型ガードでSurveyResponseTypeかどうかを確認
      if ('survey' in res) {
        // 固定質問の場合は編集不可として一覧に戻す
        if (res.survey.isFixed) {
          alert('固定質問は編集できません。')
          history.push('/setting/survey')
          return
        }

        // 各言語のデータを初期化
        const currentLanguage = i18n.language || 'ja'
        const multiLangTitle = res.survey.title || {}
        const multiLangContent = res.survey.question_text || {}
        const multiLangInputValue: { [key: string]: string[] } = {}

        // 各言語の選択肢データを初期化
        const languages = ['ja', 'en', 'zh', 'ko']
        languages.forEach(lang => {
          multiLangInputValue[lang] = res.survey.choices?.map((choice: any) => choice[lang] || choice.ja || choice.text || '') || [
            '選択肢1',
          ]
        })

        setMultiLangData({
          title: multiLangTitle,
          content: multiLangContent,
          inputValue: multiLangInputValue,
        })
        setOriginalData(res.survey)

        // 現在の言語のデータを表示
        const title = multiLangTitle[currentLanguage] || ''
        const content = multiLangContent[currentLanguage] || ''
        const inputValue = multiLangInputValue[currentLanguage] || ['選択肢1']

        setTitle(title)
        setRawContent(content)
        setContent(content)
        // Convert old inputType to new format
        const convertedInputType: 'score' | 'choice_single' | 'choice_multiple' | 'textArea' =
          res.survey.inputType === 'none'
            ? 'score'
            : res.survey.inputType === 'textArea'
            ? 'textArea'
            : res.survey.inputType === 'selectBox'
            ? res.survey.type === 'choice_multiple'
              ? 'choice_multiple'
              : 'choice_single'
            : 'choice_single'
        setInputType(convertedInputType)
        setInputValue(inputValue)
        setIsRequired(Boolean(res.survey.isRequired))

        // refも更新
        updateRef(title, content, inputValue)
        setDetailLoadedId(surveyId)
        setIsInitialLoading(false)
      } else if ('question' in res) {
        // 新しいAPI形式の場合
        const question = res.question as any

        // 固定質問の場合は編集不可として一覧に戻す
        if (question.isFixed) {
          alert('固定質問は編集できません。')
          history.push('/setting/survey')
          return
        }

        // 各言語のデータを初期化
        const currentLanguage = i18n.language || 'ja'
        const multiLangTitle = question.title || {}
        const multiLangContent = question.questionText || {}
        const multiLangInputValue: { [key: string]: string[] } = {}

        // 各言語の選択肢データを初期化
        const languages = ['ja', 'en', 'zh', 'ko']
        languages.forEach(lang => {
          multiLangInputValue[lang] = question.choices?.map((choice: any) => choice[lang] || choice.ja || choice.text || '') || ['選択肢1']
        })

        setMultiLangData({
          title: multiLangTitle,
          content: multiLangContent,
          inputValue: multiLangInputValue,
        })
        setOriginalData(question)

        // 現在の言語のデータを表示
        const title = multiLangTitle[currentLanguage] || ''
        const content = multiLangContent[currentLanguage] || ''
        const inputValue = multiLangInputValue[currentLanguage] || ['選択肢1']

        setTitle(title)
        setRawContent(content)
        setContent(content)
        // Convert API inputType to frontend format
        const convertedInputType: 'score' | 'choice_single' | 'choice_multiple' | 'textArea' =
          question.type === 'score'
            ? 'score'
            : question.type === 'text' || question.type === 'textarea'
            ? 'textArea'
            : question.type === 'choice_single'
            ? 'choice_single'
            : question.type === 'choice_multiple'
            ? 'choice_multiple'
            : 'choice_single'
        setInputType(convertedInputType)
        setInputValue(inputValue)
        setIsRequired(Boolean(question.isRequired))

        // refも更新
        updateRef(title, content, inputValue)
        setDetailLoadedId(surveyId)
        setIsInitialLoading(false)
      } else {
        setIsInitialLoading(false)
      }
    } catch (error) {
      console.error('Failed to fetch survey:', error)
      if (isMounted) {
        alert('設問の取得に失敗しました。')
        history.push('/setting/survey')
      }
      setIsInitialLoading(false)
    }
  }

  const changeIsRequired = () => {
    setIsRequired(!isRequired)
  }

  // 言語変更時の処理（編集内容は破棄して、その言語の既存データを表示）
  const handleLanguageChange = useCallback((newLanguage: string) => {
    console.log('=== 言語変更開始 ===')
    console.log('言語変更検知:', {
      currentLang: i18n.language,
      newLanguage,
    })

    // 編集内容は破棄して、その言語の既存データを表示
    setMultiLangData(prev => {
      console.log('既存のmultiLangData:', prev)

      // 新しい言語のデータを表示（既存のデータのみ）
      const newTitle = prev.title[newLanguage] || ''
      const newContent = prev.content[newLanguage] || ''
      const newInputValue = prev.inputValue[newLanguage] || ['選択肢1']

      console.log('新しい言語のデータ:', {
        newTitle,
        newContent,
        newInputValue,
      })

      console.log('状態更新実行:', { newTitle, newContent, newInputValue })

      setTitle(newTitle)
      setContent(newContent)
      setRawContent(newContent)
      setInputValue(newInputValue)

      // refも更新
      updateRef(newTitle, newContent, newInputValue)

      console.log('=== 言語変更完了 ===')

      return prev // 既存データは変更しない
    })
  }, [])

  const updateSurvey = async () => {
    try {
      setIsLoading(true)

      // 現在の入力値を現在の言語で保存
      const currentLang = i18n.language || 'ja'
      const updatedMultiLangData = {
        title: { ...multiLangData.title, [currentLang]: title },
        content: { ...multiLangData.content, [currentLang]: content },
        inputValue: { ...multiLangData.inputValue, [currentLang]: inputValue },
      }
      setMultiLangData(updatedMultiLangData)

      // バリデーション
      if (!title.trim()) {
        alert('項目名を入力してください。')
        return
      }
      if (!content.trim()) {
        alert('質問文を入力してください。')
        return
      }

      // 選択式の場合のバリデーション
      if (inputType === 'choice_single' || inputType === 'choice_multiple') {
        const validChoices = inputValue.filter(v => v && v.trim() !== '')
        console.log('選択肢バリデーション:', {
          inputType,
          inputValue,
          validChoices,
          validChoicesLength: validChoices.length,
        })

        if (validChoices.length === 0) {
          alert('選択肢を入力してください。')
          return
        }
      }

      // 新しいAPIのデータ形式に変換
      const convertInputType = (type: string): 'score' | 'choice_single' | 'choice_multiple' | 'text' => {
        switch (type) {
          case 'score':
            return 'score'
          case 'choice_single':
            return 'choice_single'
          case 'choice_multiple':
            return 'choice_multiple'
          case 'textArea':
            return 'text'
          default:
            return 'choice_single'
        }
      }

      const requestData = {
        title: title.trim(),
        questionText: content.trim(),
        type: convertInputType(inputType),
        choices:
          inputType === 'choice_single' || inputType === 'choice_multiple' ? inputValue.filter(v => v && v.trim() !== '') : undefined,
        isRequired,
        isVisible: true, // 編集時は表示状態を維持
      }

      if (urlId) {
        // 編集の場合
        await Api.editSurvey(urlId, requestData, currentLang)
      } else {
        // 新規作成の場合
        await Api.createSurvey(requestData, currentLang)
      }

      // コンポーネントがアンマウントされている場合は処理を停止
      if (!isMounted) return

      history.push({ pathname: '/setting/survey' })
    } catch (e) {
      if (isMounted) {
        errorHandler(e)
      }
    } finally {
      if (isMounted) {
        setIsLoading(false)
      }
    }
  }

  useSettingsEditPageGuardEffect({
    redirectPath: '/setting/survey',
    onAllowed: () => {
      if (urlId) {
        fetchSurvey(urlId)
      } else {
        // 新規作成の場合は初期ローディングを終了
        setIsInitialLoading(false)
      }

      return () => {
        setIsMounted(false)
      }
    },
  })

  // 言語変更を監視して入力フィールドを更新
  useEffect(() => {
    console.log('useEffect 言語変更:', {
      i18nLanguage: i18n.language,
      originalData: !!originalData,
      isInitialLoading,
      shouldExecute: originalData && !isInitialLoading,
      multiLangData,
    })

    if (originalData && !isInitialLoading) {
      console.log('handleLanguageChangeを実行します')
      handleLanguageChange(i18n.language || 'ja')
    }
  }, [i18n.language, originalData, isInitialLoading])

  const footerContent = <EditFooter onCancel={history.goBack} onSave={updateSurvey} />
  return (
    <SettingsLayout loading={isLoading || isInitialLoading} footerContent={footerContent}>
      <TabContainer tabComponent={<SurveyTab currentTab="Item" />} isShowFooter={true}>
        <div css={formContainerStyle}>
          <div css={formFieldGroupStyle}>
            <div css={formFieldSectionStyle}>
              <label htmlFor="title" css={labelStyle}>
                {t('Item name')}
                <span css={requiredMarkStyle}>※</span>
              </label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={e => {
                  const value = e.target.value
                  setTitle(value)
                  currentValuesRef.current.title = value
                  currentValuesRef.current.content = content
                  currentValuesRef.current.inputValue = inputValue
                }}
                css={inputStyle}
                maxLength={10}
                placeholder={t('Overall satisfaction with your stay')}
              />
            </div>

            <div css={formFieldSectionStyle}>
              <label htmlFor="question" css={labelStyle}>
                {t('Question text')}
                <span css={requiredMarkStyle}>※</span>
              </label>
              <input
                type="text"
                id="question"
                value={content?.replace(/<[^>]*>/g, '') || ''}
                onChange={e => {
                  const value = e.target.value
                  setContent(value)
                  setRawContent(value)
                  currentValuesRef.current.title = title
                  currentValuesRef.current.content = value
                  currentValuesRef.current.inputValue = inputValue
                }}
                css={inputStyle}
                placeholder="今回の滞在にどの程度満足されましたか？"
              />
            </div>
          </div>

          <div css={formFieldStyle}>
            <label css={labelStyle}>
              {t('Answer format')}
              <span css={requiredMarkStyle}>※</span>
            </label>
            <div css={radioGroupContainerStyle}>
              <RadioGroupField<typeof inputType>
                value={inputType}
                onChange={urlId ? () => {} : v => setInputType(v.target.value)} // 編集時はonChangeを無効化
                items={[
                  { value: 'score', label: t('Score format') },
                  { value: 'choice_single', label: t('Single selection') },
                  { value: 'choice_multiple', label: t('Multiple selection') },
                  { value: 'textArea', label: t('Free description') },
                ]}
                itemCss={{
                  marginRight: 28,
                  '& input[type="radio"]': {
                    width: '16px',
                    height: '16px',
                  },
                  ...(urlId && {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    pointerEvents: 'none',
                  }),
                }}
                style={{ labelMargin: 12 }}
              />
            </div>
          </div>

          {(inputType === 'choice_single' || inputType === 'choice_multiple') && (
            <div css={formFieldStyle}>
              <label css={labelStyle}>{t('Options')}</label>
              <div css={optionsContainerStyle}>
                <div css={optionsListStyle}>
                  {inputValue.map((option, index) => (
                    <div key={index} css={optionItemStyle}>
                      <span css={optionNumberStyle}>{index + 1}</span>
                      <input
                        type="text"
                        value={option}
                        onChange={e => {
                          const newInputValue = [...inputValue]
                          newInputValue[index] = e.target.value
                          setInputValue(newInputValue)
                          currentValuesRef.current.title = title
                          currentValuesRef.current.content = content
                          currentValuesRef.current.inputValue = newInputValue
                        }}
                        css={optionInputStyle}
                        placeholder="選択肢を入力"
                      />
                      {index > 0 && (
                        <img
                          src={require('@/static/images/icon_delete2.svg')}
                          onClick={() => {
                            const newInputValue = inputValue.filter((_, i) => i !== index)
                            // 削除後に空の選択肢が残らないように、最低1つは残す
                            if (newInputValue.length === 0) {
                              const finalValue = ['選択肢1']
                              setInputValue(finalValue)
                              currentValuesRef.current.title = title
                              currentValuesRef.current.content = content
                              currentValuesRef.current.inputValue = finalValue
                            } else {
                              setInputValue(newInputValue)
                              currentValuesRef.current.title = title
                              currentValuesRef.current.content = content
                              currentValuesRef.current.inputValue = newInputValue
                            }
                          }}
                          css={deleteOptionIconStyle}
                          alt="Delete"
                        />
                      )}
                    </div>
                  ))}
                  <div css={optionItemStyle}>
                    <span css={optionNumberStyle}>{inputValue.length + 1}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const newInputValue = [...inputValue, '']
                        setInputValue(newInputValue)
                        currentValuesRef.current.title = title
                        currentValuesRef.current.content = content
                        currentValuesRef.current.inputValue = newInputValue
                      }}
                      css={addOptionButtonStyle}
                    >
                      {t('Add option')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div css={formFieldStyle}>
            <label css={labelStyle}>{t('Answer')}</label>
            <label
              css={css`
                display: flex;
                alignitems: center;
                cursor: pointer;
                margintop: 8px;
              `}
            >
              <input type="checkbox" checked={isRequired} onChange={changeIsRequired} css={checkboxStyle} />
              <span css={checkboxTextStyle}>{t('Required')}</span>
            </label>
          </div>
        </div>
      </TabContainer>
    </SettingsLayout>
  )
}

const formContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '50%',
  padding: 24,
  gap: 16,
})

const formFieldGroupStyle = css({
  width: 518,
  height: 136,
  opacity: 1,
  display: 'flex',
  flexDirection: 'column',
})

const formFieldSectionStyle = css({
  width: 518,
  height: 56,
  opacity: 1,
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 20,
})

const formFieldStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
})

const labelStyle = css({
  display: 'block',
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  textAlign: 'left',
  opacity: 1,
  marginBottom: 12,
})

const requiredMarkStyle = css({
  color: '#676767',
  fontSize: 8,
  fontWeight: 'bold',
  letterSpacing: '0.4px',
  textAlign: 'left',
  opacity: 1,
  position: 'relative',
  top: '-4px',
  left: '2px',
})

const inputStyle = css({
  width: '100%',
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 16px',
  fontSize: 12,
  backgroundColor: '#FFFFFF',
  '::placeholder': {
    color: '#CCCCCC',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#F2A40B',
  },
})

const textareaStyle = css({
  width: '100%',
  minHeight: 60,
  border: '1px solid #CCCCCC',
  borderRadius: 12,
  padding: '8px 12px',
  fontSize: 12,
  backgroundColor: '#FFFFFF',
  resize: 'vertical',
  fontFamily: 'inherit',
  '::placeholder': {
    color: '#CCCCCC',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#F2A40B',
  },
})

const optionsContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
})

const optionItemStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
  '&:last-child': {
    marginBottom: 0,
  },
})

const optionInputStyle = css({
  flex: 1,
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 16px',
  fontSize: 12,
  backgroundColor: '#FFFFFF',
  '::placeholder': {
    color: '#CCCCCC',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#F2A40B',
  },
})

const deleteOptionIconStyle = css({
  width: 20,
  height: 20,
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.7,
  },
})

const addOptionButtonStyle = css({
  height: 32,
  border: 'none',
  borderRadius: 16,
  padding: '0 16px',
  fontSize: 14,
  backgroundColor: 'transparent',
  color: '#F2A40B',
  fontWeight: 'normal',
  cursor: 'pointer',
  textAlign: 'left',
  '&:hover': {
    backgroundColor: '#FFF8E1',
  },
})

const checkboxLabelStyle = css({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
})

const checkboxStyle = css({
  width: 16,
  height: 16,
  marginRight: 8,
  cursor: 'pointer',
})

const checkboxTextStyle = css({
  fontSize: 12,
  color: '#272727',
})

const radioGroupContainerStyle = css({
  marginTop: 8,
  width: 518, // 質問文のテキストボックスと同じ幅
})

const optionsListStyle = css({
  border: '1px solid #E0E0E0',
  borderRadius: 16,
  padding: 16,
  marginBottom: 8,
  width: 518,
})

const optionNumberStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  fontSize: 10,
  fontWeight: 'bold',
  marginRight: 8,
  flexShrink: 0,
  color: '#333333',
})
